"use client";

import { useEffect, useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { LoadingSpinner } from "@/components/ui/loading";
import { useSearchComposer, type ComposerField } from "./useSearchComposer";
// 패널은 지연 로드(공용 번들 제외) — 유휴 시간에 미리 받는다. lazy-panels.tsx 참고.
import { DatePanel, LocationPanel, WhatPanel, preloadSearchPanels } from "./lazy-panels";

export type MobileTrigger = "pill" | "icon" | "summary";

/**
 * 모바일 검색 — 트리거(홈 알약 / 내비 돋보기 / 검색 결과의 요약 알약) + 전체 화면 시트.
 * 시트는 위치 → 날짜 → 무엇을 카드가 하나씩 펼쳐지고, 하단 [전체 삭제] [검색] 고정.
 * `open`/`onOpenChange` 를 주면 바깥(홈 내비의 스크롤 돋보기)에서도 연다.
 */
export function MobileSearch({
  trigger,
  open: openProp,
  onOpenChange,
}: {
  trigger: MobileTrigger;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const t = useTranslations("searchComposer");
  const api = useSearchComposer();
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const [step, setStep] = useState<ComposerField>("location");

  useEffect(() => preloadSearchPanels(), []);

  const setOpen = (next: boolean) => {
    if (next) {
      // 열 때마다 지금 URL 로 초안을 다시 채운다.
      api.reset();
      setStep("location");
    }
    setOpenState(next);
    onOpenChange?.(next);
  };

  // 바깥에서 open=true 로 열린 경우에도 초안이 URL 과 맞도록 — 열림 전이에서 한 번.
  const [seenOpen, setSeenOpen] = useState(open);
  if (open !== seenOpen) {
    setSeenOpen(open);
    if (open && openProp !== undefined) {
      api.reset();
      setStep("location");
    }
  }

  const submit = async () => {
    if (await api.submit()) setOpen(false);
  };

  const location = api.locationLabel(api.draft.location);

  return (
    <>
      {trigger === "icon" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t("search")}
          className="flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-gray-100"
        >
          <MagnifyingGlassIcon className="size-5" />
        </button>
      ) : trigger === "pill" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex h-13 w-full items-center justify-center gap-2 rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-900 shadow-[0_4px_14px_rgba(0,0,0,0.1)]"
        >
          <MagnifyingGlassIcon className="size-4.5" weight="bold" />
          {t("mobileEntry")}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          // 검색 결과 상단(09-28, 에어비앤비 방식) — 가운데 두 줄: 위치 / 날짜 · 무엇을
          className="flex h-14 w-full min-w-0 flex-col items-center justify-center rounded-full border border-gray-200 bg-white px-5 shadow-[0_2px_10px_rgba(0,0,0,0.08)]"
        >
          <span className="max-w-full truncate text-sm font-semibold text-gray-900">
            {location ?? t("compactWhere")}
          </span>
          <span className="mt-0.5 max-w-full truncate text-xs text-gray-500">
            {api.dateLabel ?? t("compactWhen")} · {api.whatLabel ?? t("compactWhat")}
          </span>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton={false}
          className="inset-0 top-0 left-0 flex h-dvh w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 bg-gray-100 p-0 sm:max-w-none data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100"
        >
          <VisuallyHidden>
            <DialogTitle>{t("search")}</DialogTitle>
          </VisuallyHidden>
          <div className="flex shrink-0 items-center px-4 pb-2 pt-safe">
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("close")}
              className="mt-2 flex size-9 items-center justify-center rounded-full border border-gray-300 bg-white"
            >
              <XIcon className="size-4" weight="bold" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
            <Card
              active={step === "location"}
              title={t("location")}
              value={location ?? t("compactWhere")}
              onOpen={() => setStep("location")}
            >
              <LocationPanel
                api={api}
                mobile
                onPicked={() => setStep("date")}
                onRecent={(href) => {
                  setOpen(false);
                  api.openRecent(href);
                }}
              />
            </Card>
            <Card
              active={step === "date"}
              title={t("date")}
              value={api.dateLabel ?? t("compactWhen")}
              onOpen={() => setStep("date")}
            >
              <DatePanel api={api} mobile />
            </Card>
            <Card
              active={step === "what"}
              title={t("what")}
              value={api.whatLabel ?? t("compactWhat")}
              onOpen={() => setStep("what")}
            >
              <WhatPanel api={api} mobile onNavigate={() => setOpen(false)} onSubmit={submit} />
            </Card>
          </div>

          <div className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-white px-5 pt-3.5 pb-[max(0.875rem,var(--loc-safe-area-inset-bottom))]">
            <button type="button" onClick={api.clearAll} className="text-[15px] font-medium text-gray-900 underline">
              {t("clearAll")}
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={api.submitting}
              className="flex h-12 items-center gap-2 rounded-xl bg-orange-500 px-6 text-[15px] font-semibold text-white hover:bg-orange-600"
            >
              {api.submitting ? (
                <LoadingSpinner size="sm" className="size-4 text-white" />
              ) : (
                <MagnifyingGlassIcon className="size-4.5" weight="bold" />
              )}
              {t("search")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Card({
  active,
  title,
  value,
  onOpen,
  children,
}: {
  active: boolean;
  title: string;
  value: string;
  onOpen: () => void;
  children: ReactNode;
}) {
  if (!active) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="mb-2.5 flex w-full items-center justify-between gap-4 rounded-2xl bg-white px-4 py-4 text-left text-sm shadow-[0_1px_3px_rgba(0,0,0,0.06)]"
      >
        <span className="shrink-0 text-gray-500">{title}</span>
        <span className="min-w-0 truncate font-semibold text-gray-900">{value}</span>
      </button>
    );
  }
  return (
    <section className="mb-2.5 rounded-3xl bg-white px-4 pb-4 pt-5 shadow-[0_6px_18px_rgba(0,0,0,0.08)]">
      <h2 className="mb-3 text-[22px] font-bold text-gray-900">{title}</h2>
      {children}
    </section>
  );
}
